import { Schema } from "effect";

import { EnvironmentId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

export const COMPUTER_AUTOMATION_OPERATIONS = [
  "status",
  "requestControl",
  "releaseControl",
  "snapshot",
  "click",
  "type",
  "press",
  "scroll",
] as const;

export const ComputerAutomationOperation = Schema.Literals(COMPUTER_AUTOMATION_OPERATIONS);
export type ComputerAutomationOperation = typeof ComputerAutomationOperation.Type;

export const ComputerAutomationClientId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type ComputerAutomationClientId = typeof ComputerAutomationClientId.Type;

export const ComputerAutomationConnectionId = TrimmedNonEmptyString.check(Schema.isMaxLength(64));
export type ComputerAutomationConnectionId = typeof ComputerAutomationConnectionId.Type;

export const ComputerAutomationDisplayId = TrimmedNonEmptyString.check(Schema.isMaxLength(256));
export type ComputerAutomationDisplayId = typeof ComputerAutomationDisplayId.Type;

const PositiveFinite = Schema.Finite.check(Schema.isGreaterThan(0));
const NonNegativeFinite = Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0));
const PositiveCoordinateSpaceSize = PositiveFinite.annotate({
  description:
    "Width or height of the snapshot coordinate space used to choose the target. The host scales this space to the current display.",
});

export const ComputerAutomationDisplay = Schema.Struct({
  displayId: ComputerAutomationDisplayId,
  label: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(256))),
  primary: Schema.Boolean,
  bounds: Schema.Struct({
    x: Schema.Finite,
    y: Schema.Finite,
    width: PositiveFinite,
    height: PositiveFinite,
  }),
  scaleFactor: PositiveFinite,
});
export type ComputerAutomationDisplay = typeof ComputerAutomationDisplay.Type;

export const ComputerAutomationPermissionState = Schema.Literals([
  "notDetermined",
  "denied",
  "granted",
  "restricted",
  "unavailable",
]);
export type ComputerAutomationPermissionState = typeof ComputerAutomationPermissionState.Type;

export const ComputerAutomationControlState = Schema.Struct({
  state: Schema.Literals(["released", "pending", "granted"]),
  expiresAt: Schema.NullOr(
    Schema.String.annotate({
      description: "ISO-8601 expiration timestamp for the current consent lease.",
    }),
  ),
});
export type ComputerAutomationControlState = typeof ComputerAutomationControlState.Type;

export const ComputerAutomationStatusInput = Schema.Struct({
  includeDisplays: Schema.optional(
    Schema.Boolean.annotate({
      description: "Include current display metadata. Defaults to true.",
    }),
  ),
});
export type ComputerAutomationStatusInput = typeof ComputerAutomationStatusInput.Type;

export const ComputerAutomationStatus = Schema.Struct({
  available: Schema.Boolean,
  platform: Schema.Literals(["darwin", "win32", "linux"]),
  permissions: Schema.Struct({
    screenCapture: ComputerAutomationPermissionState,
    accessibility: ComputerAutomationPermissionState,
  }),
  control: ComputerAutomationControlState,
  displays: Schema.Array(ComputerAutomationDisplay),
});
export type ComputerAutomationStatus = typeof ComputerAutomationStatus.Type;

export const ComputerAutomationRequestControlInput = Schema.Struct({
  reason: Schema.String.check(Schema.isTrimmed())
    .check(
      Schema.isNonEmpty({
        description:
          "Short user-facing explanation of the computer task that needs temporary control.",
      }),
    )
    .check(
      Schema.isMaxLength(500, {
        description:
          "Short user-facing explanation of the computer task that needs temporary control.",
      }),
    ),
  durationSeconds: Schema.optional(
    Schema.Int.check(Schema.isGreaterThan(0)).check(Schema.isLessThanOrEqualTo(300)).annotate({
      description: "Requested control duration in seconds. Defaults to 60; maximum 300.",
    }),
  ),
}).annotate({
  description:
    "Requests an explicit, expiring user consent lease for this environment, thread, and provider session.",
});
export type ComputerAutomationRequestControlInput =
  typeof ComputerAutomationRequestControlInput.Type;

export const ComputerAutomationReleaseControlInput = Schema.Struct({
  reason: Schema.optional(
    Schema.String.check(Schema.isTrimmed())
      .check(
        Schema.isNonEmpty({
          description: "Optional user-facing reason for releasing control.",
        }),
      )
      .check(Schema.isMaxLength(500)),
  ).annotate({ description: "Optional user-facing reason for releasing control." }),
});
export type ComputerAutomationReleaseControlInput =
  typeof ComputerAutomationReleaseControlInput.Type;

export const ComputerAutomationSnapshotInput = Schema.Struct({
  displayId: Schema.optional(ComputerAutomationDisplayId).annotate({
    description: "Display to capture. Omit to capture the primary display.",
  }),
});
export type ComputerAutomationSnapshotInput = typeof ComputerAutomationSnapshotInput.Type;

export const ComputerAutomationSnapshot = Schema.Struct({
  capturedAt: Schema.String,
  display: ComputerAutomationDisplay,
  screenshot: Schema.Struct({
    mimeType: Schema.Literal("image/png"),
    data: Schema.String,
    width: Schema.Int.check(Schema.isGreaterThan(0)),
    height: Schema.Int.check(Schema.isGreaterThan(0)),
  }),
});
export type ComputerAutomationSnapshot = typeof ComputerAutomationSnapshot.Type;

export const ComputerAutomationClickInput = Schema.Struct({
  displayId: Schema.String.check(Schema.isTrimmed())
    .check(
      Schema.isNonEmpty({
        description: "Stable identifier of the host display to click.",
      }),
    )
    .check(
      Schema.isMaxLength(256, {
        description: "Stable identifier of the host display to click.",
      }),
    ),
  x: NonNegativeFinite.annotate({
    description: "Horizontal coordinate in the supplied snapshot coordinate space.",
  }),
  y: NonNegativeFinite.annotate({
    description: "Vertical coordinate in the supplied snapshot coordinate space.",
  }),
  coordinateSpaceWidth: PositiveCoordinateSpaceSize,
  coordinateSpaceHeight: PositiveCoordinateSpaceSize,
  button: Schema.optional(
    Schema.Literals(["left", "right", "middle"]).annotate({
      description: "Mouse button to click. Defaults to left.",
    }),
  ).annotate({ description: "Mouse button to click. Defaults to left." }),
  clickCount: Schema.optional(
    Schema.Int.check(Schema.isGreaterThan(0))
      .check(Schema.isLessThanOrEqualTo(3))
      .annotate({ description: "Number of clicks from 1 through 3. Defaults to 1." }),
  ).annotate({ description: "Number of clicks from 1 through 3. Defaults to 1." }),
})
  .check(
    Schema.makeFilter(
      (input) =>
        (input.x <= input.coordinateSpaceWidth && input.y <= input.coordinateSpaceHeight) ||
        "Click coordinates must be inside the supplied coordinate space.",
    ),
  )
  .annotate({
    description:
      "Clicks a display coordinate from a recent computer snapshot. The host maps the supplied coordinate space to the current display.",
  });
export type ComputerAutomationClickInput = typeof ComputerAutomationClickInput.Type;

export const ComputerAutomationTypeInput = Schema.Struct({
  text: Schema.String.check(Schema.isMaxLength(10_000)).annotate({
    description:
      "Literal text to type into the currently focused host application. Maximum 10000 characters.",
  }),
});
export type ComputerAutomationTypeInput = typeof ComputerAutomationTypeInput.Type;

export const ComputerAutomationKeyModifier = Schema.Literals(["Alt", "Control", "Meta", "Shift"]);
export type ComputerAutomationKeyModifier = typeof ComputerAutomationKeyModifier.Type;

export const ComputerAutomationPressInput = Schema.Struct({
  key: Schema.String.check(Schema.isTrimmed())
    .check(
      Schema.isNonEmpty({
        description:
          "Keyboard key such as Enter, Escape, Tab, ArrowDown, Backspace, or one character.",
      }),
    )
    .check(Schema.isMaxLength(64)),
  modifiers: Schema.optional(
    Schema.Array(ComputerAutomationKeyModifier)
      .check(Schema.isMaxLength(4))
      .annotate({ description: "Modifier keys held while pressing key." }),
  ),
});
export type ComputerAutomationPressInput = typeof ComputerAutomationPressInput.Type;

export const ComputerAutomationScrollInput = Schema.Struct({
  displayId: Schema.String.check(Schema.isTrimmed())
    .check(
      Schema.isNonEmpty({
        description: "Stable identifier of the host display to scroll.",
      }),
    )
    .check(Schema.isMaxLength(256)),
  deltaX: Schema.optional(
    Schema.Finite.annotate({
      description: "Horizontal scroll delta. Positive scrolls right.",
    }),
  ),
  deltaY: Schema.optional(
    Schema.Finite.annotate({
      description: "Vertical scroll delta. Positive scrolls down.",
    }),
  ),
  x: Schema.optional(
    NonNegativeFinite.annotate({
      description: "Optional horizontal anchor in the supplied snapshot coordinate space.",
    }),
  ).annotate({
    description: "Optional horizontal anchor in the supplied snapshot coordinate space.",
  }),
  y: Schema.optional(
    NonNegativeFinite.annotate({
      description: "Optional vertical anchor in the supplied snapshot coordinate space.",
    }),
  ).annotate({
    description: "Optional vertical anchor in the supplied snapshot coordinate space.",
  }),
  coordinateSpaceWidth: Schema.optional(PositiveCoordinateSpaceSize),
  coordinateSpaceHeight: Schema.optional(PositiveCoordinateSpaceSize),
})
  .check(
    Schema.makeFilter((input) => {
      if (input.deltaX === undefined && input.deltaY === undefined) {
        return "Provide deltaX or deltaY.";
      }
      const anchorCount = [
        input.x,
        input.y,
        input.coordinateSpaceWidth,
        input.coordinateSpaceHeight,
      ].filter((value) => value !== undefined).length;
      if (anchorCount !== 0 && anchorCount !== 4) {
        return "A scroll anchor requires x, y, coordinateSpaceWidth, and coordinateSpaceHeight.";
      }
      if (
        input.x !== undefined &&
        input.y !== undefined &&
        input.coordinateSpaceWidth !== undefined &&
        input.coordinateSpaceHeight !== undefined &&
        (input.x > input.coordinateSpaceWidth || input.y > input.coordinateSpaceHeight)
      ) {
        return "Scroll coordinates must be inside the supplied coordinate space.";
      }
      return true;
    }),
  )
  .annotate({
    description:
      "Scrolls at the current pointer, or at a display coordinate when the full snapshot coordinate space is supplied.",
  });
export type ComputerAutomationScrollInput = typeof ComputerAutomationScrollInput.Type;

export const ComputerAutomationHostIdentity = Schema.Struct({
  clientId: ComputerAutomationClientId,
  environmentId: EnvironmentId,
});
export type ComputerAutomationHostIdentity = typeof ComputerAutomationHostIdentity.Type;

export const ComputerAutomationHost = Schema.Struct({
  ...ComputerAutomationHostIdentity.fields,
  supportedOperations: Schema.optional(Schema.Array(ComputerAutomationOperation)),
});
export type ComputerAutomationHost = typeof ComputerAutomationHost.Type;

export const ComputerAutomationHostFocus = Schema.Struct({
  ...ComputerAutomationHostIdentity.fields,
  connectionId: ComputerAutomationConnectionId,
  focused: Schema.Boolean,
});
export type ComputerAutomationHostFocus = typeof ComputerAutomationHostFocus.Type;

/**
 * All authority fields are populated by the server from the provider-scoped
 * MCP credential. A renderer host must treat them as the consent-lease binding,
 * never accept equivalent values from model-authored tool input.
 */
export const ComputerAutomationRequest = Schema.Struct({
  requestId: TrimmedNonEmptyString,
  environmentId: EnvironmentId,
  threadId: ThreadId,
  providerSessionId: TrimmedNonEmptyString,
  providerInstanceId: ProviderInstanceId,
  operation: ComputerAutomationOperation,
  input: Schema.Unknown,
  timeoutMs: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type ComputerAutomationRequest = typeof ComputerAutomationRequest.Type;

export const ComputerAutomationStreamEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("connected"),
    connectionId: ComputerAutomationConnectionId,
  }),
  Schema.Struct({
    type: Schema.Literal("request"),
    connectionId: ComputerAutomationConnectionId,
    request: ComputerAutomationRequest,
  }),
]);
export type ComputerAutomationStreamEvent = typeof ComputerAutomationStreamEvent.Type;

export const ComputerAutomationResponse = Schema.Struct({
  clientId: ComputerAutomationClientId,
  connectionId: ComputerAutomationConnectionId,
  requestId: TrimmedNonEmptyString,
  ok: Schema.Boolean,
  result: Schema.optional(Schema.Unknown),
  error: Schema.optional(
    Schema.Struct({
      _tag: TrimmedNonEmptyString,
      message: Schema.String,
      detail: Schema.optional(Schema.Unknown),
    }),
  ),
});
export type ComputerAutomationResponse = typeof ComputerAutomationResponse.Type;

export class ComputerAutomationUnavailableError extends Schema.TaggedErrorClass<ComputerAutomationUnavailableError>()(
  "ComputerAutomationUnavailableError",
  {
    capability: Schema.Literal("computer"),
    environmentId: EnvironmentId,
    threadId: ThreadId,
    providerSessionId: TrimmedNonEmptyString,
    providerInstanceId: ProviderInstanceId,
  },
) {
  override get message(): string {
    return `MCP credential does not grant the ${this.capability} capability.`;
  }
}

const ComputerAutomationScopeErrorFields = {
  operation: ComputerAutomationOperation,
  environmentId: EnvironmentId,
  threadId: ThreadId,
  providerSessionId: TrimmedNonEmptyString,
  providerInstanceId: ProviderInstanceId,
};

const ComputerAutomationRequestErrorFields = {
  ...ComputerAutomationScopeErrorFields,
  clientId: ComputerAutomationClientId,
  connectionId: ComputerAutomationConnectionId,
  requestId: TrimmedNonEmptyString,
  timeoutMs: Schema.Int.check(Schema.isGreaterThan(0)),
};

const ComputerAutomationRemoteDiagnosticFields = {
  remoteTag: TrimmedNonEmptyString,
  remoteMessageLength: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  remoteDetailKind: Schema.optional(
    Schema.Literals(["null", "array", "object", "string", "number", "boolean"]),
  ),
  cause: Schema.Defect(),
};

const ComputerAutomationOptionalRemoteDiagnosticFields = {
  remoteTag: Schema.optional(TrimmedNonEmptyString),
  remoteMessageLength: Schema.optional(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
  remoteDetailKind: Schema.optional(
    Schema.Literals(["null", "array", "object", "string", "number", "boolean"]),
  ),
  cause: Schema.optional(Schema.Defect()),
};

export class ComputerAutomationNoAvailableHostError extends Schema.TaggedErrorClass<ComputerAutomationNoAvailableHostError>()(
  "ComputerAutomationNoAvailableHostError",
  {
    ...ComputerAutomationScopeErrorFields,
    clientId: Schema.optional(ComputerAutomationClientId),
    connectionId: Schema.optional(ComputerAutomationConnectionId),
    requestId: Schema.optional(TrimmedNonEmptyString),
    timeoutMs: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))),
    ...ComputerAutomationOptionalRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `No computer automation host is available for ${this.operation} in environment ${this.environmentId}.`;
  }
}

export class ComputerAutomationUnsupportedClientError extends Schema.TaggedErrorClass<ComputerAutomationUnsupportedClientError>()(
  "ComputerAutomationUnsupportedClientError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation client ${this.clientId} does not support ${this.operation}.`;
  }
}

export class ComputerAutomationPermissionDeniedError extends Schema.TaggedErrorClass<ComputerAutomationPermissionDeniedError>()(
  "ComputerAutomationPermissionDeniedError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
    permission: Schema.optional(Schema.Literals(["screenCapture", "accessibility"])),
  },
) {
  override get message(): string {
    return this.permission === undefined
      ? `Computer automation ${this.operation} requires a host permission.`
      : `Computer automation ${this.operation} requires the ${this.permission} permission.`;
  }
}

export class ComputerAutomationConsentDeniedError extends Schema.TaggedErrorClass<ComputerAutomationConsentDeniedError>()(
  "ComputerAutomationConsentDeniedError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `The user declined computer control for ${this.operation}.`;
  }
}

export class ComputerAutomationControlRequiredError extends Schema.TaggedErrorClass<ComputerAutomationControlRequiredError>()(
  "ComputerAutomationControlRequiredError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation ${this.operation} requires an active consent lease.`;
  }
}

export class ComputerAutomationControlInterruptedError extends Schema.TaggedErrorClass<ComputerAutomationControlInterruptedError>()(
  "ComputerAutomationControlInterruptedError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation ${this.operation} was interrupted on client ${this.clientId}.`;
  }
}

export class ComputerAutomationTimeoutError extends Schema.TaggedErrorClass<ComputerAutomationTimeoutError>()(
  "ComputerAutomationTimeoutError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationOptionalRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation ${this.operation} timed out after ${this.timeoutMs}ms.`;
  }
}

export class ComputerAutomationExecutionError extends Schema.TaggedErrorClass<ComputerAutomationExecutionError>()(
  "ComputerAutomationExecutionError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation ${this.operation} failed on client ${this.clientId}.`;
  }
}

export class ComputerAutomationResultTooLargeError extends Schema.TaggedErrorClass<ComputerAutomationResultTooLargeError>()(
  "ComputerAutomationResultTooLargeError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
    maximumBytes: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))),
  },
) {
  override get message(): string {
    return this.maximumBytes === undefined
      ? `Computer automation ${this.operation} produced a result that is too large.`
      : `Computer automation ${this.operation} produced a result larger than ${this.maximumBytes} bytes.`;
  }
}

export class ComputerAutomationClientDisconnectedError extends Schema.TaggedErrorClass<ComputerAutomationClientDisconnectedError>()(
  "ComputerAutomationClientDisconnectedError",
  ComputerAutomationRequestErrorFields,
) {
  override get message(): string {
    return `Computer automation client ${this.clientId} disconnected during ${this.operation}.`;
  }
}

export class ComputerAutomationRequestQueueClosedError extends Schema.TaggedErrorClass<ComputerAutomationRequestQueueClosedError>()(
  "ComputerAutomationRequestQueueClosedError",
  ComputerAutomationRequestErrorFields,
) {
  override get message(): string {
    return `Computer automation client ${this.clientId} stopped accepting ${this.operation} requests.`;
  }
}

export class ComputerAutomationRemoteUnavailableError extends Schema.TaggedErrorClass<ComputerAutomationRemoteUnavailableError>()(
  "ComputerAutomationRemoteUnavailableError",
  {
    ...ComputerAutomationRequestErrorFields,
    ...ComputerAutomationRemoteDiagnosticFields,
  },
) {
  override get message(): string {
    return `Computer automation ${this.operation} is unavailable on client ${this.clientId}.`;
  }
}

export class ComputerAutomationMalformedResponseError extends Schema.TaggedErrorClass<ComputerAutomationMalformedResponseError>()(
  "ComputerAutomationMalformedResponseError",
  ComputerAutomationRequestErrorFields,
) {
  override get message(): string {
    return `Computer automation client ${this.clientId} returned a malformed response for ${this.operation}.`;
  }
}

export const ComputerAutomationError = Schema.Union([
  ComputerAutomationUnavailableError,
  ComputerAutomationNoAvailableHostError,
  ComputerAutomationUnsupportedClientError,
  ComputerAutomationPermissionDeniedError,
  ComputerAutomationConsentDeniedError,
  ComputerAutomationControlRequiredError,
  ComputerAutomationControlInterruptedError,
  ComputerAutomationTimeoutError,
  ComputerAutomationExecutionError,
  ComputerAutomationResultTooLargeError,
  ComputerAutomationClientDisconnectedError,
  ComputerAutomationRequestQueueClosedError,
  ComputerAutomationRemoteUnavailableError,
  ComputerAutomationMalformedResponseError,
]);
export type ComputerAutomationError = typeof ComputerAutomationError.Type;
